import { useCallback } from 'react';
import {
  answerCall as answerCallSdk,
  rejectCall as rejectCallSdk,
} from '../../services/cxone-sdk.service';
import { logger } from '../../utils/logger';
import { CallState } from '../../types';
import { initialCallState } from './types';

interface UseAcceptRejectCallOptions {
  contactId: string | null;
  handleSessionError: (error: Error) => void;
  setCallState: React.Dispatch<React.SetStateAction<CallState>>;
  setIsRinging: React.Dispatch<React.SetStateAction<boolean>>;
  setCallError: React.Dispatch<React.SetStateAction<string | null>>;
  setAgentLegId: React.Dispatch<React.SetStateAction<string | null>>;
  setAgentLegStatus: React.Dispatch<React.SetStateAction<string | null>>;
  setIsAgentLegActive: React.Dispatch<React.SetStateAction<boolean>>;
}

function markCallActive(setCallState: React.Dispatch<React.SetStateAction<CallState>>): void {
  setCallState((prev) => ({
    ...prev,
    status: 'active',
    startTime: prev.startTime || new Date(),
  }));
}

export function useAcceptRejectCall({
  contactId,
  handleSessionError,
  setCallState,
  setIsRinging,
  setCallError,
  setAgentLegId,
  setAgentLegStatus,
  setIsAgentLegActive,
}: UseAcceptRejectCallOptions) {
  const acceptCall = useCallback(async () => {
    if (!contactId) {
      logger.warn('[Events] No contact ID to accept');
      return;
    }

    logger.debug('[Events] Accepting call, contactId:', contactId);

    try {
      await answerCallSdk(contactId);
      logger.lifecycle('[Call] Accept successful', { contactId });
      setIsRinging(false);
      setCallError(null);
      markCallActive(setCallState);
    } catch (error) {
      logger.error('[Events] Failed to accept call:', error);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const httpStatus = (error as any)?.data?.status || (error as any)?.status;
      if (httpStatus === 409) {
        logger.lifecycle('[Call] Accept already completed', { contactId });
        setIsRinging(false);
        setCallError(null);
        markCallActive(setCallState);
      } else {
        setCallError('Failed to accept the call. Please try again.');
        if (error instanceof Error) {
          handleSessionError(error);
        }
      }
    }
  }, [contactId, handleSessionError, setCallError, setCallState, setIsRinging]);

  const rejectCall = useCallback(async () => {
    if (!contactId) {
      logger.warn('[Events] No contact ID to reject');
      return;
    }

    try {
      await rejectCallSdk(contactId);
      logger.lifecycle('[Call] Reject successful', { contactId });
      setIsRinging(false);
      setCallError(null);
      setCallState(initialCallState);
      setAgentLegId(null);
      setAgentLegStatus(null);
      setIsAgentLegActive(false);
    } catch (error) {
      logger.error('[Events] Failed to reject call:', error);
      setCallError('Failed to reject the call. Please try again.');
      if (error instanceof Error) {
        handleSessionError(error);
      }
    }
  }, [
    contactId,
    handleSessionError,
    setAgentLegId,
    setAgentLegStatus,
    setCallError,
    setCallState,
    setIsAgentLegActive,
    setIsRinging,
  ]);

  return { acceptCall, rejectCall };
}
